import styled from '@emotion/styled';
import React, {useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {
  Block,
  Check,
  LoadingIcon,
  VisibilityOff,
  VisibilityOn,
} from '@ELib/assets/images';
import {useMountEffect} from '@ELib/utilities/hooks/effectHelpers';
import {PasswordValidityState} from '@ELib/utilities/types';

import {AnimationWrapper} from '../AnimationWrapper';
import {EIconButton} from '../EButtons';
import {ETooltip} from '../ETooltip';

import {ETextInput, ETextInputProps} from './ETextInput';

export type EPasswordFieldProps = Omit<
  ETextInputProps,
  'type' | 'endAdornment'
> & {
  validityState?: PasswordValidityState;
};

export const EPasswordField = ({
  validityState = PasswordValidityState.NONE,
  ...props
}: EPasswordFieldProps): React.ReactElement => {
  const [hasFocus, setHasFocus] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showCapsLockWarning, setShowCapsLockWarning] = useState(false);

  useMountEffect(() => {
    const handleKeyDown = (evt: KeyboardEvent) => {
      const capsLockActive = evt.getModifierState('CapsLock');
      setShowCapsLockWarning(
        evt.key === 'CapsLock' ? !capsLockActive : capsLockActive
      );
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  });

  const validityAdornment = useMemo(() => {
    switch (validityState) {
      case PasswordValidityState.INVALID:
        return <WrappedBlock />;
      case PasswordValidityState.LOADING:
        return (
          <AnimationWrapper>
            <WrappedLoading />
          </AnimationWrapper>
        );
      case PasswordValidityState.VALID:
        return <WrappedCheck />;
      case PasswordValidityState.NONE:
      default:
        return null;
    }
  }, [validityState]);

  return (
    <ETextInput
      endAdornment={
        <>
          {validityAdornment}
          <PasswordAdornment
            hasFocus={hasFocus}
            setShowPassword={setShowPassword}
            showCapsLockWarning={showCapsLockWarning}
            showPassword={showPassword}
          />
        </>
      }
      type={showPassword ? 'text' : 'password'}
      onBlur={() => setHasFocus(false)}
      onFocus={() => setHasFocus(true)}
      {...props}
    />
  );
};

const PasswordAdornment = ({
  showPassword,
  showCapsLockWarning,
  hasFocus,
  setShowPassword,
}: {
  showPassword: boolean;
  showCapsLockWarning: boolean;
  hasFocus: boolean;
  setShowPassword: (show: boolean) => void;
}) => {
  const {t} = useTranslation('lib');
  return (
    <>
      {/* Wrapping the tooltip around the icon button leads to rendering problems. */}
      <ETooltip
        open={(showCapsLockWarning && hasFocus) || false}
        title={t('login.warnings.capslockWarning')}
      >
        <div />
      </ETooltip>
      <EIconButton onClick={() => setShowPassword(!showPassword)}>
        {showPassword ? <VisibilityOn /> : <VisibilityOff />}
      </EIconButton>
    </>
  );
};

const WrappedCheck = styled(Check)`
  width: var(--icon-dimension);
  height: var(--icon-dimension);
  fill: var(--color-alerts-success);
`;

const WrappedBlock = styled(Block)`
  width: var(--icon-dimension);
  height: var(--icon-dimension);
  fill: var(--color-alerts-error);
`;

const WrappedLoading = styled(LoadingIcon)`
  fill: var(--core-core-grey);
`;
